/**
 * Group sessions repository.
 *
 * Owns `group_sessions` (ET4/ET5/ET6 group therapy sessions) and
 * `group_session_members` (the attendance roster per session).
 *
 * Runtime-Typisierung: Dieselbe Strategie wie src/data/reporting.ts –
 * die Rueckgaben aus PostgREST-Joins (`patient:patients(...)` /
 * `member_count:group_session_members(count)`) werden per Zod-Schema
 * validiert, weil die supabase-js-Typgenerierung Join-Aliase nicht
 * inferiert. Insert/Update-Shapes nutzen die generierten Tables*-Typen.
 *
 * Siehe docs/audit-2026-04-22.md, Findings #29 / Q-6.
 */

import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { unwrap } from '@/lib/supabase-helpers';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export type GroupSessionInsert = TablesInsert<'group_sessions'>;
export type GroupSessionUpdate = TablesUpdate<'group_sessions'>;
export type GroupMemberInsert = TablesInsert<'group_session_members'>;
export type GroupMemberUpdate = TablesUpdate<'group_session_members'>;

// ── Row-Schemas ─────────────────────────────────────────────────────────────

const UpcomingGroupSessionSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  scheduled_at: z.string().nullable(),
  hmv_code: z.string().nullable().optional(),
  status: z.string().nullable(),
  max_participants: z.number().nullable().optional(),
  duration_minutes: z.number().nullable().optional(),
  member_count: z
    .array(z.object({ count: z.number() }))
    .nullable()
    .optional(),
});
export type UpcomingGroupSession = z.infer<typeof UpcomingGroupSessionSchema>;

const GroupMemberSchema = z.object({
  id: z.string(),
  session_id: z.string(),
  patient_id: z.string().nullable().optional(),
  attendance_status: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  billed: z.boolean().nullable().optional(),
  created_at: z.string().nullable().optional(),
  patient: z
    .object({
      first_name: z.string().nullable().optional(),
      last_name: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type GroupMember = z.infer<typeof GroupMemberSchema>;

function parseRows<T>(schema: z.ZodType<T>, data: unknown, source: string): T[] {
  if (!Array.isArray(data)) return [];
  const out: T[] = [];
  let dropped = 0;
  for (const row of data) {
    const parsed = schema.safeParse(row);
    if (parsed.success) out.push(parsed.data);
    else dropped += 1;
  }
  if (dropped > 0) {
    // Keine PHI im Log – nur Zahl + Funktionsname.
    console.warn(`[groupSessions] ${source}: ${dropped} Zeilen verworfen (Schema mismatch)`);
  }
  return out;
}

// ── Queries ─────────────────────────────────────────────────────────────────

export async function listGroupSessions(orgId: string) {
  return unwrap(
    supabase
      .from('group_sessions')
      .select('*')
      .eq('organization_id', orgId)
      .order('scheduled_at', { ascending: false }),
  );
}

export async function listUpcomingGroupSessions(
  orgId: string,
  limit = 5,
): Promise<UpcomingGroupSession[]> {
  // Aggregate-style join `member_count:group_session_members(count)` is
  // not inferred by the generated types. `parseRows` validates the
  // shape at runtime against `UpcomingGroupSessionSchema`.
  const { data, error } = await supabase
    .from('group_sessions')
    .select(
      'id, title, scheduled_at, hmv_code, status, max_participants, duration_minutes, member_count:group_session_members(count)',
    )
    .eq('organization_id', orgId)
    .in('status', ['geplant', 'aktiv'])
    .order('scheduled_at', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return parseRows(UpcomingGroupSessionSchema, data, 'listUpcomingGroupSessions');
}

// ── Mutations ───────────────────────────────────────────────────────────────

export async function createGroupSession(input: GroupSessionInsert) {
  return unwrap(
    supabase.from('group_sessions').insert([input]).select('*').single(),
  );
}

export async function addGroupMember(input: GroupMemberInsert) {
  const { error } = await supabase.from('group_session_members').insert([input]);
  if (error) throw error;
}

export async function updateGroupMember(memberId: string, patch: GroupMemberUpdate) {
  const { error } = await supabase
    .from('group_session_members')
    .update(patch)
    .eq('id', memberId);
  if (error) throw error;
}

export async function updateGroupSession(id: string, patch: GroupSessionUpdate) {
  const { error } = await supabase.from('group_sessions').update(patch).eq('id', id);
  if (error) throw error;
}

export async function listGroupMembers(sessionId: string): Promise<GroupMember[]> {
  // The PostgREST `select('*, patient:patients(...)')` returns a row
  // whose nested `patient` is not inferred by the generated types. The
  // result is shaped at runtime by `parseRows` against
  // `GroupMemberSchema`, so the static type is intentionally `unknown`
  // here — no `any` cast needed.
  const { data, error } = await supabase
    .from('group_session_members')
    .select('*, patient:patients(first_name, last_name)')
    .eq('session_id', sessionId);
  if (error) throw error;
  return parseRows(GroupMemberSchema, data, 'listGroupMembers');
}
